import { Button } from '@aecom-ai-engineering/react-components';
import { useAcceptInviteMutation } from '@consigli/hooks';
import {
  type InviteOrganization,
  type InviteProject,
  OrganizationRole,
  ProjectRole,
} from '@consigli/types';
import { ShieldCheck } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { formatISODate } from '@/util/date';

const ROW = 'flex items-center text-sm leading-5';
const LABEL = 'flex-1 font-medium text-[var(--ds-color-text-primary)]';
const VALUE = 'flex-1 text-[color:var(--ds-color-raw-stone-600)]';

interface ProfileInviteCardProps {
  expiresAt: string;
  id: string;
  organization?: InviteOrganization | null;
  projects?: InviteProject[] | null;
}

/**
 * One pending invitation, and the button that accepts it.
 *
 * The behaviour is the card this replaces, unchanged: `acceptInvite` by id, an
 * info toast on success and an error toast on failure, and the same dd/MM/yyyy
 * expiry. What changed is the surface — the rows read like the rows in the
 * cards above rather than as their own small layout.
 *
 * The button is disabled while the request is in flight, where the old card
 * replaced itself with a spinner. Both stop a second accept; this one does not
 * take the invitation off the screen while it is still being accepted.
 */
export const ProfileInviteCard: FC<ProfileInviteCardProps> = ({
  expiresAt,
  id,
  organization,
  projects,
}) => {
  const { t } = useTranslation();
  const [acceptInvite, { isLoading: accepting }] = useAcceptInviteMutation();

  const onAccept = useCallback(async () => {
    try {
      await acceptInvite(id).unwrap();
      toast.info(t('profile.accepted-success'));
    } catch {
      toast.error(t('profile.accepted-failure'));
    }
  }, [acceptInvite, id, t]);

  const isOrgAdmin = organization?.role === OrganizationRole.ADMIN;
  const invited = projects ?? [];

  return (
    <article className="flex flex-col gap-4 rounded-[var(--ds-radius-8)] border border-[var(--ds-color-border-primary)] p-4">
      <div className="flex items-start justify-between gap-4">
        <dl className="flex min-w-0 flex-1 flex-col gap-2">
          <div className={ROW}>
            <dt className={LABEL}>{t('profile.role')}</dt>
            <dd className={VALUE}>{isOrgAdmin ? t('profile.admin') : t('profile.user')}</dd>
          </div>
          <div className={ROW}>
            <dt className={LABEL}>{t('profile.expires-at')}</dt>
            <dd className={VALUE}>{formatISODate(expiresAt, 'dd/MM/yyyy')}</dd>
          </div>
          {/* What the invitation grants, and only where the old card said it:
              an organisation membership below admin. */}
          {organization && !isOrgAdmin && (
            <div className={ROW}>
              <dt className={LABEL}>{t('profile.invite-can-create-projects')}</dt>
              <dd className={VALUE}>{t('profile.yes')}</dd>
            </div>
          )}
        </dl>

        <Button disabled={accepting} onClick={() => void onAccept()} size="sm" variant="solid">
          {t('profile.accept')}
        </Button>
      </div>

      {invited.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] leading-[18px] text-[color:var(--ds-color-raw-stone-600)]">
            {t('profile.projects')}
          </p>

          <ul className="flex flex-col gap-2">
            {invited.map((project) => (
              <li
                className="flex flex-col gap-1 rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-secondary)] p-3"
                key={project.id}
              >
                <div className="flex items-center gap-1">
                  <span className="truncate text-sm leading-5 font-medium text-[var(--ds-color-text-primary)]">
                    {project.name}
                  </span>
                  {project.role === ProjectRole.ADMIN && (
                    <>
                      {/* Decorative — the words are beside it, as the crown on
                          the banner is. */}
                      <ShieldCheck
                        aria-hidden="true"
                        className="size-4 shrink-0 text-[color:var(--ds-color-raw-stone-600)]"
                        strokeWidth={1.5}
                      />
                      <span className="text-[13px] leading-[18px] text-[color:var(--ds-color-raw-stone-600)]">
                        {t('profile.project-admin')}
                      </span>
                    </>
                  )}
                </div>

                <p className="text-[13px] leading-[18px] text-[color:var(--ds-color-raw-stone-600)]">
                  {t('profile.service-access')}{' '}
                  {project.permissions.length > 0
                    ? project.permissions
                        .map((permission) => t(`profile.services.${permission}`))
                        .join(', ')
                    : t('profile.all-services')}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
};
